import { virtualDir } from "./skyward.base.global";
import { getOriginalValue, getValue, toggle } from "./skyward.ui.controls";
import { registerEvents } from "./skyward.ui.events";
import { loadContent } from "./skyward.ui.window";
import { ButtonDefinition, JQueryInput, ModalDialog, ValidationData } from "./types";

const skyward = window.skyward;

const events =
{
    toggleGrayScaleStyle
};

registerEvents("themeSelector", events)

const classNames =
{
    themeSelectionContainer: "themeSelectionContainer",
    themeSelectionTileBodyDisabledIcon: "themeSelectionTileBody__disabledIcon",
    themeSelectionContainerThemeName: "themeSelectionContainer__themeName",
    themeSelectionContainerActive: "themeSelectionContainer--active",
    selectedUserThemeButton: "js-selectedUserThemeButton",
    skywardTheme: "skywardTheme",
    browseTileContainer: "browseTileContainer",
    tempCss: "tempCss"
};

const dataNames =
{
    themeId: "themeId",
    cssHash: "cssHash",
};

let dialog: ModalDialog;
let pageStylesheet = $(".themeStyleSheet");
let changeThemeButton: JQuery<HTMLElement>;

let warnAboutGrayScale = false;
const grayScaleWarning: ValidationData =
{
    errors: {},
    warnings:
    {
        "": ["Grayscale filer has been selected. All pages and their content will be displayed on a grayscale."]
    }
};

let preventThemeSelection = false;
let applyGrayScaleFilterCheckbox: JQuery<HTMLElement>;

export function buildThemeSelector(button: JQueryInput)
{
    changeThemeButton = $(button);
    const saveButton: ButtonDefinition = { text: "Save", click: saveUsersTheme, icon: "Save" };
    const cancelButton: ButtonDefinition = { text: "Cancel", click: revertToPreviousTheme, icon: "Cancel" };
    const options = { width: "54%", transparentGlass: true, draggable: true, closeOnClick: false, dynamicDialogContentSelector: "#ThemeSelectionTileBrowse" };

    $.when(getAllThemes()).always(function (response)
    {
        const result = $("<div>");
        result.html(response);

        dialog = skyward.modalLayer.showDialog("Themes", () => result, [saveButton, cancelButton], options);

        $.when(dialog.dynamicContentLoadedPromise).then(() =>
        {
            $("#ThemeSelectionTileBrowse").on("click", "a", applyTheme);

            applyGrayScaleFilterCheckbox = $("#browseTileContainer__applyGrayScaleFilterCheckbox");
            applyGrayScaleFilterCheckbox.on("change", onApplyGrayScaleCheckboxClicked);

            highlightTheme();
            onApplyGrayScaleCheckboxClicked();
        });
    });

    function getAllThemes()
    {
        return $.ajax(
            {
                type: "POST",
                url: `${virtualDir}SkySys/Theme/GetThemesForSelection`
            });
    }

    function revertToPreviousTheme()
    {
        $(".tempCss").remove();

        const originalValue = getOriginalValue(applyGrayScaleFilterCheckbox);
        if (getValue(applyGrayScaleFilterCheckbox) != originalValue)
        {
            setGrayscaleFilter(originalValue ? 1 : 0);
        }

        $(document.body).trigger("themeSwap");

        dialog.close();
    }
};

function toggleGrayScaleStyle(this: JQueryInput)
{
    const grayScaleEnabled = $(this).is(":checked");
    setGrayscaleFilter(grayScaleEnabled ? 1 : 0);
}

function applyTheme(this: JQueryInput)
{
    if (preventThemeSelection === true)
    {
        return;
    }

    const currentElement = $(this);
    const canEnableGrayScale = currentElement.children(`.${classNames.skywardTheme}`).length > 0;
    toggle(applyGrayScaleFilterCheckbox, canEnableGrayScale);

    const themeId = currentElement.data(dataNames.themeId) as string;
    const cssHash = currentElement.data(dataNames.cssHash) as string;

    updateStylesheet(themeId, cssHash);
    highlightTheme(themeId, currentElement);
}

function onApplyGrayScaleCheckboxClicked(_e?: JQuery.Event, override?: boolean)
{
    const apply = override ?? applyGrayScaleFilterCheckbox.is(":checked");
    $(`.${classNames.themeSelectionTileBodyDisabledIcon}`).toggle(apply);
    $(`.${classNames.browseTileContainer}`).toggleClass("disabled noHover", apply);
    setGrayscaleFilter(apply ? 1 : 0);

    preventThemeSelection = Boolean(apply);
    warnAboutGrayScale = Boolean(apply);
}

function setGrayscaleFilter(amount: number)
{
    $(document.documentElement).css({ filter: `grayscale(${amount})` });
}

function saveUsersTheme()
{
    const tempCss = $(`.${classNames.tempCss}`);
    const grayScaleCheckboxIsDirty = getValue(applyGrayScaleFilterCheckbox) != getOriginalValue(applyGrayScaleFilterCheckbox);
    if (tempCss.length !== 1 && !grayScaleCheckboxIsDirty)
    {
        dialog.close();
        return;
    }

    const currentCSSThemeId = tempCss.data(dataNames.themeId);

    if (warnAboutGrayScale === true)
    {
        const container = $("#themeSelectionModalContent__errorContainer");
        skyward.validation.displayValidationMessages(grayScaleWarning, container, container);
        warnAboutGrayScale = false;
        return;
    }

    $.ajax(
        {
            type: "POST",
            dataType: "text",
            url: `${virtualDir}Common/Css/SaveUsersTheme`,
            data:
            {
                tid: currentCSSThemeId,
                applyGrayScaleFilter: applyGrayScaleFilterCheckbox.is(":checked"),
            }
        }).done(() => finishSave(tempCss))
}

function finishSave(tempCss: JQuery<HTMLElement>)
{
    if (tempCss.length === 1)
    {
        pageStylesheet.remove();

        tempCss.removeClass(classNames.tempCss);
        tempCss.attr("id", "ThemeCSS");

        pageStylesheet = tempCss;
    }

    dialog.close();

    const data = changeThemeButton.data();
    if (data.reloadAfterSave)
    {
        loadContent(data.target, data.area, data.controller, data.action, data.id);
    }
}

function updateStylesheet(themeId: string, cssHash: string)
{
    if (pageStylesheet.data(dataNames.themeId) !== themeId || pageStylesheet.data(dataNames.cssHash) !== cssHash)
    {
        const styleSheetHref = `${virtualDir}Common/Css/Theme/${themeId}/${cssHash}`;

        const tempStyle = $("<link rel='stylesheet' type='text/css' />");
        tempStyle.addClass(classNames.tempCss);
        tempStyle.data(dataNames.themeId, themeId);
        tempStyle.data(dataNames.cssHash, cssHash);
        tempStyle.attr("href", styleSheetHref);

        tempStyle.on("load", function ()
        {
            $(`.${classNames.tempCss}`).slice(1).remove();
            $(document.body).trigger("themeSwap");
        });

        pageStylesheet.after(tempStyle);
    }
    else
    {
        $(`.${classNames.tempCss}`).remove();
    }
}

function highlightTheme(themeId?: string, currentElement?: JQuery<HTMLElement>)
{
    if (themeId === undefined)
    {
        const tempCss = $(`.${classNames.tempCss}`);
        themeId = (tempCss.length === 1) ? tempCss.data(dataNames.themeId) : pageStylesheet.data(dataNames.themeId);
    }

    if (currentElement === undefined)
    {
        currentElement = $(`.${classNames.themeSelectionContainer}:focus`);
    }

    const themeLabel = currentElement.find(`.${classNames.themeSelectionContainerThemeName}`);
    const previousElement = $(`.${classNames.themeSelectionContainerActive}`);
    const prevThemeLabel = previousElement.find(`.${classNames.themeSelectionContainerThemeName}`);

    if (currentElement.data(dataNames.themeId) === themeId && !currentElement.hasClass(classNames.themeSelectionContainerActive))
    {
        const previewLabel = " (PREVIEW)";
        currentElement.addClass(classNames.themeSelectionContainerActive);
        if (!currentElement.hasClass(classNames.selectedUserThemeButton))
        {
            themeLabel.html(`${themeLabel.text()}${previewLabel}`);
        }
        previousElement.removeClass(classNames.themeSelectionContainerActive);
        if (!previousElement.hasClass(classNames.selectedUserThemeButton))
        {
            prevThemeLabel.text(prevThemeLabel.text().replace(previewLabel, ""));
        }
    }

    const currentThemeLabel = $(`.${classNames.selectedUserThemeButton}`).find(`.${classNames.themeSelectionContainerThemeName}`);
    const savedLabel = "(SAVED)";
    if (currentThemeLabel.text().indexOf(savedLabel) == -1)
    {
        currentThemeLabel.text(`${currentThemeLabel.text()} ${savedLabel}`);
    }
}